import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Building2, CheckCircle2, Mail, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function VenueOwnerRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";
  
  const [step, setStep] = useState(1); // 1=form, 2=success
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [duplicateEmail, setDuplicateEmail] = useState(""); // tracks duplicate email error

  const [setupStatus, setSetupStatus] = useState(""); // "", "registering", "checking", "finalizing"

  // Helper: Poll for registration completion
  const pollForRegistration = async (email, maxAttempts = 15, intervalMs = 2000) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Use GET endpoint (simpler, no CORS preflight)
        const check = await api.get(`/venue-onboarding/check-registration?email=${encodeURIComponent(email)}`, { timeout: 10000 });
        if (check.data.exists) {
          return { success: true, userId: check.data.user_id };
        }
      } catch (err) {
        // Ignore errors during polling — just keep trying
        console.log(`[Poll] Attempt ${attempt} failed:`, err.message);
      }
      // Wait before next attempt (unless it's the last)
      if (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, intervalMs));
      }
    }
    return { success: false };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDuplicateEmail(""); // Clear any previous error
    
    // Validation
    if (!form.name.trim()) {
      toast.error("Please enter your full name.");
      return;
    }
    if (!form.email.trim() || !form.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const emailToUse = form.email.trim().toLowerCase();
    setLoading(true);
    setSetupStatus("registering");

    // ═══════════════════════════════════════════════════════
    // OPTIMISTIC POLLING REGISTRATION FLOW (Passwordless)
    // ═══════════════════════════════════════════════════════
    // 1. Fire POST /register (no password - user sets it after email verification)
    // 2. Immediately start polling GET /check-registration
    // 3. Once user exists → call POST /complete-setup to trigger email
    // 4. Show success - user will set password after verifying email

    // Step 1: Fire registration request — DO NOT AWAIT the response
    try {
      const registerPromise = api.post("/venue-onboarding/register", {
        name: form.name.trim(),
        email: emailToUse,
        phone: form.phone.trim(),
      }, { timeout: 15000 });

      // Race: either get a response or timeout after 5 seconds
      const raceResult = await Promise.race([
        registerPromise.then(res => ({ type: "response", res })),
        new Promise(resolve => setTimeout(() => resolve({ type: "timeout" }), 5000)),
      ]);

      if (raceResult.type === "response") {
        const res = raceResult.res;
        if (!res.data.success) {
          toast.error("Registration failed. Please try again.");
          setLoading(false);
          setSetupStatus("");
          return;
        }
      }
    } catch (err) {
      // Check for 4xx client errors (validation, duplicate email)
      if (err.response?.status && err.response.status >= 400 && err.response.status < 500) {
        const detail = err.response?.data?.detail || "";
        if (detail.toLowerCase().includes("already exists")) {
          setDuplicateEmail(emailToUse);
          setLoading(false);
          setSetupStatus("");
          return;
        }
        const msg = typeof detail === "string" ? detail : "Registration failed. Please try again.";
        toast.error(msg);
        setLoading(false);
        setSetupStatus("");
        return;
      }
      console.log("[Register] Request error (proceeding to poll):", err.message);
    }

    // Step 2: Poll for registration completion
    setSetupStatus("checking");
    const pollResult = await pollForRegistration(emailToUse, 15, 2000); // 15 attempts × 2s = 30s max

    if (!pollResult.success) {
      // User was never created — show error
      toast.error("Registration didn't complete. Please try again. If this persists, contact support.");
      setLoading(false);
      setSetupStatus("");
      return;
    }

    // Step 3: User exists — call complete-setup to trigger email
    setSetupStatus("finalizing");
    try {
      await api.post("/venue-onboarding/complete-setup", { email: emailToUse }, { timeout: 20000 });
    } catch (err) {
      // Even if this fails, the user is created — show success but warn about email
      console.warn("[Complete Setup] Error:", err.message);
      // Don't block success — the email might have been sent anyway
    }

    // Step 4: Success!
    setRegisteredEmail(form.email);
    setStep(2);
    toast.success("Registration successful! Please check your email.");
    setLoading(false);
    setSetupStatus("");
  };

  const handleResendEmail = async () => {
    setLoading(true);
    try {
      await api.post("/venue-onboarding/resend-verification", { email: registeredEmail });
      toast.success("Verification email sent! Please check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col">
        <img 
          src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1000&q=80"
          alt="Sports Venue" 
          className="absolute inset-0 w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-night-asphalt/80" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <div />
          <div>
            <h2 className="font-heading text-4xl text-white font-bold uppercase leading-tight mb-3">
              List Your Venue on <span className="text-[#FF6B35]">SetPlay</span>
            </h2>
            <p className="text-white/80 text-lg max-w-md mb-6">
              Join the growing network of sports facilities in the Philippines. Get discovered by thousands of players and coaches.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-white font-medium">Easy Booking Management</p>
                  <p className="text-white/60 text-sm">Manage all your court bookings in one place</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-white font-medium">Increase Visibility</p>
                  <p className="text-white/60 text-sm">Get discovered by players looking for venues nearby</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-white font-medium">Secure Payments</p>
                  <p className="text-white/60 text-sm">Accept online payments and track revenue</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-20 py-12 bg-white overflow-y-auto">
        <button 
          onClick={() => navigate("/")} 
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 self-start"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />Back to home
        </button>

        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-1">
            <img src="/logo.png" alt="SetPlay" className="h-12 w-12" />
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">SetPlay</h1>
          </div>
          <p className="text-muted-foreground text-sm mb-8">Venue Owner Registration</p>

          {/* Step 1: Registration Form */}
          {step === 1 && (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="venue-owner-register-form">
              <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-[#FF6B35]" />
                  <div>
                    <p className="font-heading font-semibold text-sm uppercase">Venue Owner Registration</p>
                    <p className="text-xs text-muted-foreground">Create your account to list your sports facility</p>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Full Name *</Label>
                <Input 
                  data-testid="vo-register-name"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Juan Dela Cruz"
                  className="h-11 rounded-none border-2 mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Email Address *</Label>
                <Input 
                  data-testid="vo-register-email"
                  type="email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  placeholder="you@email.com"
                  className="h-11 rounded-none border-2 mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Phone Number (Optional)</Label>
                <Input 
                  data-testid="vo-register-phone"
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value})}
                  placeholder="+63 9XX XXX XXXX"
                  className="h-11 rounded-none border-2 mt-1"
                />
              </div>

              <p className="text-xs text-muted-foreground bg-slate-50 p-3 rounded-lg">
                After registering, you'll receive an email to verify your address. Once verified, you'll create your password.
              </p>

              {/* Duplicate Email Error Card */}
              {duplicateEmail && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="duplicate-email-error">
                  <p className="text-sm text-amber-900 font-medium mb-1">
                    An account with <strong>{duplicateEmail}</strong> already exists.
                  </p>
                  <p className="text-xs text-amber-700 mb-3">
                    If this is your account, please log in instead. If you forgot your password, you can reset it.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => navigate("/login")}
                      className="bg-court-orange hover:bg-court-orange-hover text-white text-xs font-heading uppercase rounded-full"
                      data-testid="duplicate-go-login"
                    >
                      Log In
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/forgot-password")}
                      className="text-xs font-heading uppercase rounded-full"
                      data-testid="duplicate-reset-pw"
                    >
                      Reset Password
                    </Button>
                  </div>
                </div>
              )}

              <Button 
                data-testid="vo-register-submit"
                type="submit" 
                disabled={loading}
                className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[1px] active:translate-y-[1px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {setupStatus === "registering" && "Submitting..."}
                    {setupStatus === "checking" && "Setting up account..."}
                    {setupStatus === "finalizing" && "Finalizing..."}
                    {!setupStatus && "Creating Account..."}
                  </>
                ) : (
                  <>
                    Create Venue Owner Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <button 
                  type="button"
                  onClick={() => navigate("/auth?tab=login")}
                  className="text-court-orange hover:underline font-medium"
                >
                  Log in here
                </button>
              </p>
            </form>
          )}

          {/* Step 2: Success Message */}
          {step === 2 && (
            <div className="text-center" data-testid="vo-register-success">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="font-heading text-2xl font-bold uppercase mb-3">Check Your Email!</h2>
              
              <p className="text-muted-foreground mb-6">
                We've sent a verification link to <strong className="text-foreground">{registeredEmail}</strong>. 
                Please check your inbox and click the link to verify your email address.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> The verification link will expire in 24 hours. 
                  If you don't see the email, please check your spam folder.
                </p>
              </div>

              <div className="space-y-3">
                <Button 
                  variant="outline"
                  onClick={handleResendEmail}
                  disabled={loading}
                  className="w-full h-11 rounded-none border-2 font-heading uppercase tracking-wide"
                  data-testid="vo-resend-email"
                >
                  {loading ? "Sending..." : "Resend Verification Email"}
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/auth?tab=login")}
                  className="w-full text-sm"
                >
                  Go to Login Page
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
